import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Receipt, ChevronRight, Info } from "lucide-react";
import { PageHeader, PageShell } from "@/components/PageHeader";
import { RoleGate } from "@/components/RoleGate";
import { StatusPill } from "@/components/Chips";
import { tourDisplayLabel } from "@/lib/tourDisplayLabel";
import { api } from "@/lib/api";

const fmtRange = (start, end) => {
  const f = (s) => s ? new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";
  return `${f(start)} → ${f(end)}`;
};

/**
 * Billing workspace. Reads authoritative tour + contract state from
 * `GET /api/tours` — every column here is an existing field on the
 * tour/contract record. No invented invoices, no fake payments.
 *
 * QuickBooks is disabled at the platform level (`QUICKBOOKS_ENABLED=False`);
 * columns that would depend on QB show a "QuickBooks not connected" chip
 * instead of a synthetic number.
 */

const usd = (n) => {
  if (n == null || n === "") return "—";
  const num = typeof n === "string" ? Number(n) : n;
  if (Number.isNaN(num)) return "—";
  return num.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
};

const QB_PILL = (
  <span
    className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-chrome-mist/50 text-steel border border-coach-silver"
    data-testid="qb-not-connected"
    title="QuickBooks is not connected to All Access"
  >
    <Info size={10} /> QuickBooks not connected
  </span>
);

export default function BillingPage() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/billing/tours")
      .then((r) => setRows(r.data?.results || []))
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load billing data"));
  }, []);

  return (
    <RoleGate permission="billing.view">
      <PageShell>
        <PageHeader
          testid="page-billing"
          title={<span className="inline-flex items-center gap-2"><Receipt size={22} className="text-coach-blue" />Billing</span>}
          subtitle="Contract value, contract status, and payment schedule state for every tour with a Contract. Only authoritative internal amounts are shown — AR aging will be available when authoritative accounting/payment data is connected."
        />
        {err && (
          <div className="bg-amber-hold/10 border border-amber-hold/30 rounded-sm px-4 py-2 text-[13px] text-carbon mb-4" data-testid="billing-error">
            {err}
          </div>
        )}
        <div className="mb-3 flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-widest text-steel">
            {rows == null ? "Loading…" : `${rows.length.toLocaleString()} tour${rows.length === 1 ? "" : "s"}`}
          </div>
          {QB_PILL}
        </div>

        <div className="bg-white border border-coach-silver rounded-md overflow-hidden">
          <table className="w-full text-[13px]" data-testid="billing-table">
            <thead className="bg-road-white text-[10.5px] uppercase tracking-widest text-steel">
              <tr>
                <th className="text-left px-4 py-2">Tour</th>
                <th className="text-left px-4 py-2 w-40">Customer</th>
                <th className="text-left px-4 py-2 w-48">Dates</th>
                <th className="text-left px-4 py-2 w-28">Contract</th>
                <th className="text-left px-4 py-2 w-32">Contract Value</th>
                <th className="text-left px-4 py-2 w-32">Invoiced</th>
                <th className="text-left px-4 py-2 w-32">Outstanding</th>
                <th className="px-2 py-2 w-10" />
              </tr>
            </thead>
            <tbody>
              {rows === null && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-steel">Loading…</td></tr>
              )}
              {rows && rows.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-steel">No tours to bill.</td></tr>
              )}
              {rows && rows.map((t) => {
                const isExecuted = t.contract_status === "executed";
                const value = t.contract_total;
                const paid = t.payment_schedule_summary?.paid_amount_authoritative;
                const outstanding = t.payment_schedule_summary?.outstanding_authoritative;
                const hasAuth = t.has_authoritative_billing;
                return (
                  <tr key={t.tour_id} data-testid={`billing-row-${t.tour_id}`} className="border-t border-coach-silver/60 hover:bg-road-white">
                    <td className="px-4 py-2">
                      <Link to={`/tours/${t.tour_id}`} className="text-coach-blue hover:underline" data-testid={`billing-tour-${t.tour_id}`}>
                        {/* RC5 Item 6 — tour number hidden; visible label
                            uses the shared helper (backend also supplies
                            `tour_display_label`). Never a raw UUID. */}
                        {t.tour_display_label || tourDisplayLabel(
                          { tour_name: t.display_name },
                          { clientName: t.customer_display_name }
                        )}
                      </Link>
                    </td>
                    <td className="px-4 py-2 text-steel">
                      {t.customer_display_name || "—"}
                    </td>
                    <td className="px-4 py-2 text-steel">
                      {fmtRange(t.start_date, t.end_date)}
                    </td>
                    <td className="px-4 py-2">
                      <StatusPill status={t.contract_status || "—"} />
                      {t.source_quote_version && (
                        <div className="text-[10.5px] uppercase tracking-widest text-steel mt-1">
                          From Quote v{t.source_quote_version}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-carbon tabular-nums">{usd(value)}</td>
                    <td className="px-4 py-2 tabular-nums text-carbon">
                      {isExecuted ? (hasAuth ? usd(paid) : <span className="text-steel">{QB_PILL}</span>) : <span className="text-steel">—</span>}
                    </td>
                    <td className="px-4 py-2 tabular-nums text-carbon">
                      {isExecuted ? usd(outstanding) : (
                        <span
                          className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-amber-hold/15 text-carbon border border-amber-hold/30"
                          data-testid="billing-awaiting-execution"
                        >Awaiting execution</span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right text-steel"><ChevronRight size={14} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </PageShell>
    </RoleGate>
  );
}
